import type { WhaleProfile, WhaleRow, WhalesSegment } from '@/api/types';
import { dash, formatName } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { channelLabel, preferredChannels, rewardShare } from './whale-figures';

// The whales table as a CSV file: the rows as the page shows them (filters and sort applied),
// one column per table column, with money as plain EUR numbers so Excel can sum them.

type Cell = string | number | null;

const COLUMNS: [header: string, value: (row: WhaleRow) => Cell][] = [
  ['Player ID', (row) => toPlayerId(row.playerUuid)],
  ['Name', (row) => (row.profile ? nameOf(row.profile) : null)],
  ['Country', (row) => row.country],
  ['Status', (row) => row.profile?.status ?? null],
  ['Deposits (EUR)', (row) => money(row.depositEur)],
  ['# Dep', (row) => row.depositCount],
  ['GGR (EUR)', (row) => money(row.ggrEur)],
  ['NGR (EUR)', (row) => money(row.ngrEur)],
  ['Bets (EUR)', (row) => money(row.betEur)],
  ['Main game', (row) => row.mainGame?.gameType ?? null],
  ['Main game share %', (row) => (row.mainGame ? percent(row.mainGame.betShare) : null)],
  ['Reward (EUR)', (row) => money(row.rewardEur)],
  ['CB paid (EUR)', (row) => money(row.cashbackEur)],
  ['Bonus conv (EUR)', (row) => money(row.bonusConvertedEur)],
  ['WD paid (EUR)', (row) => money(row.withdrawalEur)],
  ['Share %', (row) => shareCell(row)],
  ['Last active', (row) => row.lastActiveDay],
  ['Last comm channel', (row) => (row.lastComm ? channelLabel(row.lastComm.channel) : null)],
  ['Last comm name', (row) => row.lastComm?.name ?? null],
  ['Last comm at (UTC)', (row) => (row.lastComm ? row.lastComm.at.slice(0, 16).replace('T', ' ') : null)],
  ['Preferred method of communication', (row) => preferredText(row)],
];

function nameOf(profile: WhaleProfile): string | null {
  const name = formatName(profile);
  return name === dash ? profile.username : name;
}

const money = (value: number) => Math.round(value * 100) / 100;
const percent = (rate: number) => Math.round(rate * 10000) / 100;

function shareCell(row: WhaleRow): Cell {
  const share = rewardShare(row);
  return share === null ? 'loss' : percent(share);
}

function preferredText(row: WhaleRow): string | null {
  const preferred = preferredChannels(row.channels);
  if (preferred.length === 0) return null;
  return preferred.map((c) => `${channelLabel(c.channel)} (${Math.round(c.share * 100)}%)`).join(' / ');
}

// Quoted when needed; text that a spreadsheet would read as a formula (=, +, -, @) is
// prefixed with an apostrophe, since names and message titles come from outside.
function csvCell(value: Cell): string {
  if (value === null) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  const text = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function whalesCsv(rows: readonly WhaleRow[]): string {
  const lines = [COLUMNS.map(([header]) => csvCell(header)).join(',')];
  rows.forEach((row) => lines.push(COLUMNS.map(([, value]) => csvCell(value(row))).join(',')));
  return lines.join('\r\n') + '\r\n';
}

// whales_<FTD from>_<FTD to>_<day>.csv; "all" for an open bound.
export function whalesCsvName(segment: WhalesSegment, from: string | null, to: string | null, today: string): string {
  return `whales_${segment}_ftd_${from ?? 'all'}_${to ?? 'all'}_${today}.csv`;
}

// The BOM makes Excel read the file as UTF-8 (names with accents).
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
